import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Container, Row, Col, Card, Alert, Form, Spinner, Badge } from "react-bootstrap";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import DynamicTable from "../../components/DynamicTable";
import { fetchGpsCoverage } from "./DataService";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const fmtMinutes = (min) => {
  if (!min && min !== 0) return "—";
  const hh = Math.floor(min / 60);
  const mm = min % 60;
  return hh > 0 ? `${hh}h${String(mm).padStart(2, "0")}` : `${mm}min`;
};

const MAX_CLUSTERS = 30;

// Regroupe les dead zones par coordonnées arrondies (~100m)
function clusterDeadZones(deadZones) {
  const map = {};
  for (const dz of deadZones) {
    const key = `${dz.lat.toFixed(3)},${dz.lng.toFixed(3)}`;
    if (!map[key]) {
      map[key] = { key, lat: dz.lat, lng: dz.lng, count: 0, livreurs: new Set(), totalGap: 0 };
    }
    map[key].count++;
    map[key].livreurs.add(dz.livreur_nom);
    map[key].totalGap += dz.gap_min;
  }
  return Object.values(map)
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_CLUSTERS)
    .map((c) => ({ ...c, livreurs: [...c.livreurs], avgGap: Math.round(c.totalGap / c.count) }));
}

async function reverseGeocode(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=fr`
    );
    const json = await res.json();
    const road = json.address?.road || json.address?.suburb || json.address?.neighbourhood || "";
    const city =
      json.address?.city || json.address?.town || json.address?.village || json.address?.municipality || "";
    return [road, city].filter(Boolean).join(", ") || json.display_name || "Adresse inconnue";
  } catch {
    return null;
  }
}

function KpiCard({ label, value, sub, color }) {
  return (
    <Card className="text-center h-100" style={{ borderTop: `3px solid ${color || "#6c757d"}` }}>
      <Card.Body className="py-3">
        <div className="text-muted small mb-1">{label}</div>
        <div className="fw-bold fs-5">{value}</div>
        {sub && <div className="text-muted small mt-1">{sub}</div>}
      </Card.Body>
    </Card>
  );
}

function CoverageBar({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-muted small">—</span>;
  const color = pct >= 80 ? "#28a745" : pct >= 50 ? "#ffc107" : "#dc3545";
  return (
    <div style={{ minWidth: 100 }}>
      <div className="progress" style={{ height: 16 }}>
        <div className="progress-bar" role="progressbar" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <small style={{ color }}>{pct}%</small>
    </div>
  );
}

function DeadZoneMarker({ dz }) {
  const [address, setAddress] = useState(null);
  const [loadingAddr, setLoadingAddr] = useState(false);

  const loadAddress = useCallback(async () => {
    if (address || loadingAddr) return;
    setLoadingAddr(true);
    const addr = await reverseGeocode(dz.lat, dz.lng);
    setAddress(addr || "Adresse indisponible");
    setLoadingAddr(false);
  }, [dz.lat, dz.lng, address, loadingAddr]);

  return (
    <CircleMarker
      center={[dz.lat, dz.lng]}
      radius={6}
      pathOptions={{ color: "#dc3545", fillColor: "#dc3545", fillOpacity: 0.7, weight: 1 }}
      eventHandlers={{ click: loadAddress }}
    >
      <Popup>
        <strong>{dz.livreur_nom}</strong>
        <br />
        Coupure de <strong>{dz.gap_min} min</strong>
        <br />
        {dz.lost_at
          ? new Date(dz.lost_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
          : ""}
        <br />
        {loadingAddr ? (
          <em className="text-muted">Chargement…</em>
        ) : address ? (
          <span className="text-muted small">{address}</span>
        ) : (
          <span className="text-muted small">Cliquer pour charger l'adresse</span>
        )}
      </Popup>
    </CircleMarker>
  );
}

function LivreursGpsCouverture() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Adresses chargées pour le tableau de zones récurrentes
  const [clusterAddresses, setClusterAddresses] = useState({});
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const cancelRef = useRef(false);

  const loadData = async (m) => {
    cancelRef.current = true; // annule le chargement d'adresses précédent
    setLoading(true);
    setError(null);
    setClusterAddresses({});
    try {
      const res = await fetchGpsCoverage(m);
      setData(res);
    } catch {
      setError("Impossible de charger les données GPS.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(month);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const livreurs = data?.livreurs ?? [];
  const deadZones = data?.dead_zones ?? [];

  // Clusters triés par récurrence
  const clusters = useMemo(() => clusterDeadZones(deadZones), [deadZones]);

  // Chargement séquentiel des adresses (1 req/s pour respecter Nominatim)
  useEffect(() => {
    if (!clusters.length) return;
    cancelRef.current = false;
    setLoadingAddresses(true);

    const loadAll = async () => {
      for (const c of clusters) {
        if (cancelRef.current) break;
        if (clusterAddresses[c.key]) continue;
        const addr = await reverseGeocode(c.lat, c.lng);
        if (cancelRef.current) break;
        if (addr) {
          setClusterAddresses((prev) => ({ ...prev, [c.key]: addr }));
        }
        // Respecter la limite 1 req/s de Nominatim
        await new Promise((r) => setTimeout(r, 1100));
      }
      setLoadingAddresses(false);
    };

    loadAll();
    return () => { cancelRef.current = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusters]);

  const kpi = useMemo(() => {
    if (!livreurs.length) return null;
    const avecCoverage = livreurs.filter((l) => l.coverage_pct !== null);
    const avgPct =
      avecCoverage.length > 0
        ? Math.round((avecCoverage.reduce((s, l) => s + l.coverage_pct, 0) / avecCoverage.length) * 10) / 10
        : null;
    const sorted = [...avecCoverage].sort((a, b) => b.coverage_pct - a.coverage_pct);
    return {
      avg_pct: avgPct,
      total_dead_zones: livreurs.reduce((s, l) => s + l.nb_dead_zones, 0),
      best: sorted[0] ?? null,
      worst: sorted[sorted.length - 1] ?? null,
    };
  }, [livreurs]);

  const mapCenter = useMemo(() => {
    if (!deadZones.length) return [43.7, 7.25];
    return [
      deadZones.reduce((s, d) => s + d.lat, 0) / deadZones.length,
      deadZones.reduce((s, d) => s + d.lng, 0) / deadZones.length,
    ];
  }, [deadZones]);

  const livreurColumns = useMemo(
    () => [
      { Header: "Livreur", accessor: "nom" },
      { Header: "Shifts", accessor: "nb_shifts" },
      { Header: "Temps shift", accessor: "total_shift_min", Cell: ({ value }) => fmtMinutes(value) },
      { Header: "Temps GPS", accessor: "covered_min", Cell: ({ value }) => fmtMinutes(value) },
      { Header: "Couverture", accessor: "coverage_pct", Cell: ({ value }) => <CoverageBar pct={value} /> },
      {
        Header: "Zones mortes",
        accessor: "nb_dead_zones",
        Cell: ({ value }) => (
          <span style={{ color: value > 0 ? "#dc3545" : "#28a745", fontWeight: "bold" }}>{value}</span>
        ),
      },
    ],
    []
  );

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="mb-0">Couverture GPS des livreurs</h4>
          <small className="text-muted">Zone morte = coupure GPS &gt; 5 min entre deux points consécutifs</small>
        </Col>
        <Col xs="auto">
          <Form.Control
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            style={{ maxWidth: 200 }}
          />
        </Col>
      </Row>

      {/* KPI Cards */}
      {kpi && (
        <Row className="g-3 mb-4">
          <Col xs={6} md={3}>
            <KpiCard
              label="Couverture moyenne"
              value={kpi.avg_pct !== null ? `${kpi.avg_pct}%` : "—"}
              color={kpi.avg_pct >= 80 ? "#28a745" : kpi.avg_pct >= 50 ? "#ffc107" : "#dc3545"}
            />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard label="Zones mortes totales" value={kpi.total_dead_zones} color="#dc3545" />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard
              label="Meilleure couverture"
              value={kpi.best ? `${kpi.best.coverage_pct}%` : "—"}
              sub={kpi.best?.nom}
              color="#28a745"
            />
          </Col>
          <Col xs={6} md={3}>
            <KpiCard
              label="Pire couverture"
              value={kpi.worst ? `${kpi.worst.coverage_pct}%` : "—"}
              sub={kpi.worst?.nom}
              color="#dc3545"
            />
          </Col>
        </Row>
      )}

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Chargement des données GPS…</p>
        </div>
      ) : (
        <>
          {/* Tableau par livreur */}
          {livreurs.length > 0 ? (
            <div className="mb-4">
              <DynamicTable
                columns={livreurColumns}
                data={livreurs}
                total={livreurs.length}
                last_page={1}
                nbr={1}
                setNbr={() => {}}
              />
            </div>
          ) : (
            <Alert variant="info">Aucune donnée GPS pour ce mois.</Alert>
          )}

          {/* Carte des zones mortes */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-geo-alt-fill text-danger me-2" />
                Carte des zones mortes
              </span>
              <small className="text-muted">{deadZones.length} point(s) — cliquer pour l'adresse</small>
            </Card.Header>
            <Card.Body className="p-0">
              {deadZones.length > 0 ? (
                <MapContainer center={mapCenter} zoom={12} style={{ height: 480, width: "100%" }} scrollWheelZoom>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  />
                  {deadZones.map((dz, i) => (
                    <DeadZoneMarker key={i} dz={dz} />
                  ))}
                </MapContainer>
              ) : (
                <div className="text-center text-muted py-5">Aucune zone morte détectée pour ce mois.</div>
              )}
            </Card.Body>
          </Card>

          {/* Zones récurrentes — liste triée */}
          {clusters.length > 0 && (
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-list-ol me-2" />
                  Zones récurrentes (top {clusters.length})
                </span>
                {loadingAddresses && (
                  <small className="text-muted d-flex align-items-center gap-2">
                    <Spinner animation="border" size="sm" />
                    Résolution des adresses…
                  </small>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-hover table-sm mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th>Rue / Ville</th>
                        <th>Occurrences</th>
                        <th>Durée moy.</th>
                        <th>Livreur(s)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clusters.map((c, i) => {
                        const addr = clusterAddresses[c.key];
                        const [rue, ville] = addr ? addr.split(", ") : [null, null];
                        return (
                          <tr key={c.key}>
                            <td className="text-muted fw-bold">{i + 1}</td>
                            <td>
                              {addr ? (
                                <>
                                  {rue && <div className="fw-semibold">{rue}</div>}
                                  {ville && <div className="text-muted small">{ville}</div>}
                                  {!rue && !ville && <span className="text-muted small">{addr}</span>}
                                </>
                              ) : (
                                <span className="text-muted small">
                                  <Spinner animation="border" size="sm" className="me-1" />
                                  Chargement…
                                </span>
                              )}
                            </td>
                            <td>
                              <Badge
                                bg={c.count >= 5 ? "danger" : c.count >= 3 ? "warning" : "secondary"}
                                text={c.count >= 3 && c.count < 5 ? "dark" : undefined}
                              >
                                {c.count}×
                              </Badge>
                            </td>
                            <td className="text-muted">{fmtMinutes(c.avgGap)}</td>
                            <td>
                              <div className="d-flex flex-wrap gap-1">
                                {c.livreurs.map((nom) => (
                                  <Badge key={nom} bg="light" text="dark" className="border">
                                    {nom}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          )}
        </>
      )}
    </Container>
  );
}

export default LivreursGpsCouverture;
